import React, { useState, useEffect, useCallback } from 'react';
import { MdHome } from 'react-icons/md';
import { useClass } from '../utils/ClassContext';

// Import all class-specific components
import ClassHome from './ClassHome';
import ERDEditor from './ERDEditor/index';
import ERDGuidelines from './ERDGuidelines';
import Notifications from './Notificiations';
import Discussions from './Discussions';
import EnvironmentVisibility from './EnvironmentVisibility';
import FeedbackLog from './FeedbackLog';
import ERDAIGuideline from './ERDAIGuideline';
import DocumentEditorAIGuideline from './DocumentEditorAIGuideline';
import FinalSubmissions from './FinalSubmissions';
import ConsentViewer from './ConsentViewer';
import QuotaManagement from './InstructorTools/QuotaManagement';
import UsersInfo from './InstructorTools/UsersInfo';
import StudentQuotaPage from './StudentQuotaPage';
import ClassManagement from './Admin/ClassManagement';
import ConsentForm from './ConsentForm';
import ApiKeyForm from './ApiKeyForm';
import DocumentEditor from './DocumentEditor';
import CodeEditor from './CodeEditor';
import ClassSettings from './ClassSettings';
import { NotificationProvider } from './common/NotificationManager';

const ClassWorkspace = ({
  username,
  userRole,
  userName,
  apiKey,
  feedbackCount,
  quotaValue,
  notificationCount,
  hasConsent,
  updateApiKey,
  updateNotificationCount,
  refreshUserStats,
  onNavigate,
  apiBaseUrl,
  hasInstructorPermissions,
  navigationData,
  notificationRefreshRef,
  triggerAllUIUpdates,
  onConsentSubmitted,
  fetchClassNotificationCount,
  decrementNotificationCount,
  rollbackNotificationCount,
  currentPage
}) => {
  const { selectedClass, isPublicView } = useClass();
  const [selectedPage, setSelectedPage] = useState('home'); // Internal state for class-specific navigation

  // For public users, treat them as students with view-only access
  const isPublicUser = userRole === 'Public' || isPublicView;
  const effectiveUserRole = isPublicUser ? 'Student' : userRole;
  const effectiveHasInstructorPermissions = isPublicUser ? false : hasInstructorPermissions;

  // Sync internal state with parent's currentPage prop
  useEffect(() => {
    if (currentPage) {
      console.log('📚 Syncing ClassWorkspace page to parent currentPage:', currentPage);
      setSelectedPage(currentPage);
    }
  }, [currentPage]);

  // Restore from localStorage only on initial mount for the same class,
  // but not if the parent already routed us to a specific page (e.g. 'home' on fresh entry)
  useEffect(() => {
    if (currentPage) return; // Parent has an explicit page — don't override it

    const storedPage = localStorage.getItem('classSelectedPage');
    const storedClass = localStorage.getItem('lastClassId');

    // Only restore if this is the same class and we have a saved page
    if (storedPage && storedClass === selectedClass?.class_id && storedPage !== 'home') {
      console.log('📚 Restoring saved page:', storedPage, 'for class:', storedClass);
      setSelectedPage(storedPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Optimistic class notification count updates - now handled by parent
  const decrementClassNotificationCount = useCallback((amount = 1) => {
    // Update global count
    if (decrementNotificationCount) {
      decrementNotificationCount(amount);
    }
  }, [decrementNotificationCount]);

  const rollbackClassNotificationCount = useCallback((amount = 1) => {
    // Rollback global count
    if (rollbackNotificationCount) {
      rollbackNotificationCount(amount);
    }
  }, [rollbackNotificationCount]);

  // Get tool settings from selected class
  const classTools = selectedClass?.tools || {};
  const isERDEditorEnabled = classTools.erd_editor?.enabled ?? true;
  const isDocumentEditorEnabled = classTools.document_editor?.enabled ?? true;

  // Note: Class-specific notification count is now managed by UnifiedSidebar in App.jsx

  // Redirect users away from disabled tools
  useEffect(() => {
    if (!selectedClass) return;

    const toolPageMappings = {
      'editor': isERDEditorEnabled,
      'guidelines': isERDEditorEnabled,
      'erd-ai-guideline': isERDEditorEnabled,
      'document-editor': isDocumentEditorEnabled
    };

    // Check if current page is for a disabled tool
    if (toolPageMappings.hasOwnProperty(selectedPage) && !toolPageMappings[selectedPage]) {
      console.log(`Redirecting from disabled tool page: ${selectedPage}`);
      setSelectedPage('home');
      localStorage.setItem('classSelectedPage', 'home');
    }
  }, [selectedClass, selectedPage, isERDEditorEnabled, isDocumentEditorEnabled]);

  // Handle class-specific navigation
  const handleClassNavigation = (pageId, data = null) => {
    setSelectedPage(pageId);
    localStorage.setItem('classSelectedPage', pageId);
    localStorage.setItem('lastClassId', selectedClass?.class_id);
    
    // If this is a global navigation, pass it up to parent
    if (['dashboard', 'settings', 'help', 'about', 'logout'].includes(pageId)) {
      onNavigate(pageId, data);
      return;
    }
    
    // For class-specific pages with navigation data, pass it up to parent too
    if (data) {
      onNavigate(pageId, data);
    }
  };

  // Reset to home when class changes (but not for public view - allow navigation)
  useEffect(() => {
    if (selectedClass && !isPublicView) {
      // Only reset if we're switching to a different class
      const storedClass = localStorage.getItem('lastClassId');
      if (storedClass !== selectedClass.class_id) {
        setSelectedPage('home');
        localStorage.setItem('classSelectedPage', 'home');
        localStorage.setItem('lastClassId', selectedClass.class_id);
      }
    } else if (selectedClass && isPublicView) {
      // For public view, just track the class but don't reset page
      localStorage.setItem('lastClassId', selectedClass.class_id);
    }
  }, [selectedClass, isPublicView]);

  // Extract assignment context from navigation data (passed when opening editors from assignments)
  const assignmentNavData = navigationData?.assignmentId ? navigationData : null;

  // Render the current class page
  const renderClassPage = () => {
    const commonProps = {
      username: isPublicUser ? '' : username,
      userRole: effectiveUserRole,
      userName,
      apiKey: isPublicUser ? null : apiKey,
      notificationCount: isPublicUser ? 0 : notificationCount,
      hasConsent: isPublicUser ? false : hasConsent,
      updateApiKey: isPublicUser ? () => {} : updateApiKey,
      updateNotificationCount: isPublicUser ? () => {} : updateNotificationCount,
      refreshUserStats: isPublicUser ? () => {} : refreshUserStats,
      onNavigate: handleClassNavigation,
      apiBaseUrl,
      hasInstructorPermissions: effectiveHasInstructorPermissions,
      isPublicView: isPublicUser
    };

    console.log('🎲 ClassWorkspace switch statement - selectedPage:', selectedPage, 'at:', new Date().toISOString());

    switch (selectedPage) {
      case 'home':
        console.log('🏠 Rendering ClassHome for selectedPage:', selectedPage);
        return <ClassHome {...commonProps} isPublicView={isPublicUser} />;

      case 'requirements':
        // Requirements page removed — requirements now live in ERD Editor's Requirements tab
        setSelectedPage('editor');
        return null;

      case 'guidelines':
        // Redirect to home if ERD Editor is disabled
        if (!isERDEditorEnabled) {
          setSelectedPage('home');
          localStorage.setItem('classSelectedPage', 'home');
          return <ClassHome {...commonProps} />;
        }
        return (
          <ERDGuidelines
            onNavigate={handleClassNavigation}
          />
        );

      case 'editor':
        // Redirect to home if ERD Editor is disabled
        if (!isERDEditorEnabled) {
          setSelectedPage('home');
          localStorage.setItem('classSelectedPage', 'home');
          return <ClassHome {...commonProps} />;
        }
        return (
          <ERDEditor
            username={isPublicUser ? '' : username}
            apiKey={isPublicUser ? null : apiKey}
            userRole={effectiveUserRole}
            refreshUserStats={isPublicUser ? () => {} : triggerAllUIUpdates}
            notificationCount={isPublicUser ? 0 : notificationCount}
            isPublicView={isPublicUser}
            assignmentId={assignmentNavData?.assignmentId}
            componentId={assignmentNavData?.componentId}
            {...commonProps}
          />
        );

      case 'notifications':
        return (
          <Notifications
            username={username}
            apiBaseUrl={apiBaseUrl}
            updateNotificationCount={updateNotificationCount}
            onNavigate={handleClassNavigation}
            onImmediateRefresh={notificationRefreshRef}
            decrementNotificationCount={decrementClassNotificationCount}
            rollbackNotificationCount={rollbackClassNotificationCount}
          />
        );

      case 'discussions':
        return (
          <Discussions
            username={username}
            userRole={userRole}
            classContext={{
              class_id: selectedClass?.class_id,
              class_code: selectedClass?.class_code
            }}
            navigationData={navigationData}
          />
        );

      case 'document-editor':
        // Redirect to home if Document Editor is disabled
        if (!isDocumentEditorEnabled) {
          setSelectedPage('home');
          localStorage.setItem('classSelectedPage', 'home');
          return <ClassHome {...commonProps} />;
        }
        return (
          <NotificationProvider>
            <DocumentEditor
              username={isPublicUser ? '' : username}
              userRole={effectiveUserRole}
              apiBaseUrl={apiBaseUrl}
              isPublicView={isPublicUser}
              apiKey={apiKey}
              assignmentId={assignmentNavData?.assignmentId}
              componentId={assignmentNavData?.componentId}
              documentId={assignmentNavData?.documentId}
            />
          </NotificationProvider>
        );

      case 'activity-tracking':
        // Only allow instructors and TAs to access activity tracking
        if (!effectiveHasInstructorPermissions) {
          setSelectedPage('home');
          localStorage.setItem('classSelectedPage', 'home');
          return <ClassHome {...commonProps} />;
        }
        // Redirect to home if Document Editor is disabled (activity tracking depends on it)
        if (!isDocumentEditorEnabled) {
          setSelectedPage('home');
          localStorage.setItem('classSelectedPage', 'home');
          return <ClassHome {...commonProps} />;
        }
        const ActivityViewer = require('./ActivityTracking').default;
        return (
          <ActivityViewer
            username={isPublicUser ? '' : username}
            userRole={effectiveUserRole}
            apiBaseUrl={apiBaseUrl}
          />
        );

      case 'code-editor':
        return (
          <CodeEditor
            username={isPublicUser ? '' : username}
            userRole={effectiveUserRole}
            apiBaseUrl={apiBaseUrl}
            classContext={{
              class_id: selectedClass?.class_id,
              class_code: selectedClass?.class_code,
              username: isPublicUser ? '' : username,
              is_public_view: isPublicUser
            }}
            isPublicView={isPublicUser}
            assignmentId={assignmentNavData?.assignmentId}
            componentId={assignmentNavData?.componentId}
            initialProjectId={assignmentNavData?.projectId}
            initialFileId={assignmentNavData?.fileId}
          />
        );

      case 'assignments':
        const Assignments = require('./Assignments').default;
        return (
          <Assignments
            userRole={userRole}
            username={username}
            currentPage={currentPage}
            onNavigate={handleClassNavigation}
          />
        );

      case 'class-settings':
        return (
          <ClassSettings
            username={username}
            userRole={userRole}
            apiBaseUrl={apiBaseUrl}
          />
        );

      case 'environment-visibility':
        return (
          <EnvironmentVisibility
            userRole={userRole}
            apiBaseUrl={apiBaseUrl}
            username={username}
          />
        );

      case 'feedback-log':
        return (
          <FeedbackLog
            username={username}
            userRole={userRole}
            apiBaseUrl={apiBaseUrl}
            onNavigate={handleClassNavigation}
          />
        );

      case 'erd-ai-guideline':
        return (
          <ERDAIGuideline userRole={effectiveUserRole} />
        );

      case 'doc-editor-ai-guideline':
        return (
          <DocumentEditorAIGuideline userRole={effectiveUserRole} />
        );

      case 'final-submissions':
        return (
          <FinalSubmissions
            {...commonProps}
          />
        );

      case 'consents':
        return (
          <ConsentViewer
            {...commonProps}
          />
        );

      case 'quota-management':
        return (
          <QuotaManagement
            {...commonProps}
          />
        );

      case 'my-quota':
        return (
          <StudentQuotaPage
            {...commonProps}
          />
        );

      case 'users-info':
        return (
          <UsersInfo
            {...commonProps}
          />
        );

      case 'class-management':
        return (
          <ClassManagement
            {...commonProps}
          />
        );

      case 'consent':
        return (
          <ConsentForm
            username={username}
            apiBaseUrl={apiBaseUrl}
            onConsentSubmitted={async () => {
              // Call the parent's consent handler which fetches profile and updates API key
              if (onConsentSubmitted) {
                await onConsentSubmitted();
              }
              // Navigate back to home after consent submission
              setSelectedPage('home');
              localStorage.setItem('classSelectedPage', 'home');
            }}
          />
        );

      case 'api-key-form':
        return (
          <ApiKeyForm
            username={username}
            onNavigate={handleClassNavigation}
            onApiKeySubmit={async (apiKey) => {
              try {
                // Update API key through parent
                await updateApiKey(apiKey);
                // Navigate back to home after successful submission
                setSelectedPage('home');
                localStorage.setItem('classSelectedPage', 'home');
                return { success: true };
              } catch (error) {
                return { success: false, error: error.message };
              }
            }}
          />
        );

      default:
        return <ClassHome {...commonProps} />;
    }
  };

  // Don't render anything if no class is selected
  if (!selectedClass) {
    console.log('⚠️ ClassWorkspace: No selectedClass, returning null');
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading class...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Main Content Area - No separate sidebar, using unified sidebar from App.jsx */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-white shadow-sm border-b border-gray-200 px-6 py-4 min-h-[72px] flex items-center">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center space-x-4">
              <h1 className="text-lg font-semibold text-gray-800 capitalize">
                {selectedPage.replace('-', ' ')}
              </h1>
              {/* Show class info */}
              {selectedClass && (
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-600">
                    {selectedClass.class_name} ({selectedClass.class_code})
                  </span>
                </div>
              )}
            </div>
            <div className="flex items-center space-x-4">
              {isPublicUser ? (
                <a
                  href="/"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                  title="Return to Home"
                >
                  <MdHome className="text-lg" />
                  <span>Home</span>
                </a>
              ) : (
                <>
                  <span className="text-sm text-gray-600">
                    Welcome, {userName.firstName} {userName.lastName}
                  </span>
                  {apiKey ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      API Connected
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                      No API Key
                    </span>
                  )}
                </>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className={`flex-1 bg-gray-100 ${
          ['code-editor', 'document-editor', 'assignments', 'activity-tracking', 'discussions', 'feedback-log'].includes(selectedPage)
            ? 'overflow-hidden'
            : 'overflow-y-auto p-6'
        }`}>
          {renderClassPage()}
        </main>
      </div>
    </div>
  );
};

export default ClassWorkspace;